import { useEffect, useState } from 'react';

/**
 * Returns `true` once the browser has gone idle after first paint.
 *
 * Use this to gate non-critical network requests (analytics, translations,
 * typography, content variants, blog grids…) so they don't compete with
 * the LCP image, fonts, and main bundle on the critical path.
 *
 * Strategy:
 *  - During SSR / before mount → false
 *  - After mount → schedule via `requestIdleCallback` (or `setTimeout`
 *    fallback) with a hard deadline so we never block forever
 */
let globalReady = false;
const listeners = new Set<() => void>();

const markReady = () => {
  if (globalReady) return;
  globalReady = true;
  listeners.forEach((cb) => cb());
  listeners.clear();
};

if (typeof window !== 'undefined') {
  const schedule = () => {
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
    if (typeof ric === 'function') {
      // Long timeout so below-the-fold queries (blog grid, intelligence_config)
      // don't compete with hero LCP & main-thread hydration on slow connections.
      ric(markReady, { timeout: 5000 });
    } else {
      setTimeout(markReady, 4000);
    }
  };
  if (document.readyState === 'complete') {
    schedule();
  } else {
    window.addEventListener('load', schedule, { once: true });
    // Hard fallback in case `load` never fires (long-running 3rd-party scripts)
    setTimeout(markReady, 7000);
  }
}

export function useAfterIdle(): boolean {
  const [ready, setReady] = useState(globalReady);
  useEffect(() => {
    if (globalReady) return;
    const cb = () => setReady(true);
    listeners.add(cb);
    return () => {
      listeners.delete(cb);
    };
  }, []);
  return ready;
}

export function isAppIdle(): boolean {
  return globalReady;
}